import React, { useCallback, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { Keyboard, SectionList, StyleSheet, View, } from 'react-native';
import { CommonActions } from '@react-navigation/native';
import Animated, { interpolateColor, useAnimatedScrollHandler, useAnimatedStyle, useSharedValue, } from 'react-native-reanimated';
import FlatListItemCard from 'design-system/FlatListItemCard';
import { AppView, Button, NavigationHeader, rem, SCROLL_INDICATOR_INSETS, SearchBar, } from 'design-system';
import StatusBar from 'design-system/StatusBar';
import useColors from 'hooks/useColors';
import { ContactItem, SelectedContactsList, } from 'features/contacts/components';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useContactInvite } from 'features/contacts/hooks/useContactInvite';
import { useMapToSections } from 'features/contacts/hooks/useMapToSections';
import { useOnSearch } from 'features/contacts/hooks/useOnSearch';
import Text from 'design-system/Text';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import { useMarginBottom } from 'hooks/useMarginBottom';
import SectionHeader from 'design-system/SectionHeader';
import { addPeopleToGroup, createGroup, getGroupById } from 'actions';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectGroupAnalytics, selectUser } from 'reducers/selectors';
import ListEmptyView from 'design-system/ListEmptyView';
import ListLoader from 'design-system/ListLoader';
import Modal from 'utils/packages/Modal';
import { Amplitude } from 'utils';
import { CREATE_GROUP_SUCCESS } from 'actions/types';
import { setDeviceSettingsKey } from 'actions/deviceSettings';
import IconSend from 'design-system/icons/IconSend';
import { useTryAdvertiseExtraMembers } from 'features/premium/hooks/useTryAdvertiseExtraMembers';
import { shareGroupMessage } from './utils/share.web';
const strings = {
    buttonContinue: 'Continue',
    buttonInvite: 'Invite',
    buttonAdd: 'Add',
    buttonRemove: 'Remove',
    buttonAdded: 'Added',
    noResultTitle: 'Try searching for another name',
    emptyTitle: 'Add friends by @EmmaTag',
    emptySubTitle: 'Search for your friends’ @EmmaTag, or share the group link with them from the group.',
    addError: 'You can’t add yourself to this group',
    shareError: 'You can’t share the payment with yourself',
};
// The screens a new group is created through, from the intro to the success screen
const CREATE_FLOW_ROUTES = [
    'GroupsIntro',
    'GroupCreate',
    'GroupInvite',
    'Success',
];
const AnimatedSectionList = Animated.createAnimatedComponent(SectionList);
const keyExtractor = (item, index) => (item.handle || item.recordID).toString() + index;
/**
 * Web version of the add friends screen. Browsers can't read the device's
 * contacts, so friends are found by searching their @EmmaTag.
 */
export const GroupInviteScreen = ({ route, navigation, }) => {
    const group = 'group' in route.params ? route.params.group : undefined;
    const name = 'name' in route.params ? route.params.name : undefined;
    const colors = useColors();
    const groupAnalytics = useAppSelector((store) => selectGroupAnalytics(store, group?.id));
    const didViewGroupsIntro = useAppSelector((store) => store.deviceSettings.didViewGroupsIntro);
    const [isFetching, setIsFetching] = useState(false);
    const dispatch = useAppDispatch();
    const { selectedContacts, onPressInvite, removeFromSelected, selectedContactsObj, } = useContactInvite();
    const { remoteContacts, searchFilter, handleSearch, isLoading, handleReset } = useOnSearch();
    const sections = useMapToSections(searchFilter, remoteContacts);
    const scrollY = useSharedValue(0);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    const tryAdvertiseExtraMembers = useTryAdvertiseExtraMembers();
    const handleSave = useCallback(async () => {
        setIsFetching(true);
        Keyboard.dismiss();
        let createdGroup;
        if (!group && name) {
            const res = await dispatch(createGroup(name));
            if (res?.type !== CREATE_GROUP_SUCCESS) {
                setIsFetching(false);
                return;
            }
            createdGroup = res.payload.group;
            Amplitude.logEvent('Created Group');
        }
        const groupId = group?.id || createdGroup?.id;
        if (selectedContacts.length > 0 && groupId) {
            const handles = selectedContacts
                .map((el) => el.handle)
                .filter((handle) => !!handle);
            await dispatch(addPeopleToGroup(groupId, handles));
            dispatch(getGroupById(groupId));
        }
        setIsFetching(false);
        if (!createdGroup) {
            navigation.pop();
            return;
        }
        const { name: groupName, joiningLink, id } = createdGroup;
        navigation.navigateDeprecated('Success', {
            title: 'Success',
            subTitle: 'The group has been created! You can\nstart splitting your bills!',
            onSecondaryButtonPress: () => {
                shareGroupMessage(`Use this link to join the group ${groupName} in Emma:\n${joiningLink}`, 'Group link copied');
                return true;
            },
            secondaryButtonText: 'Share',
            secondaryButtonLeftIcon: <IconSend color={colors.text.textOnBrand}/>,
            onContinue: () => {
                if (!didViewGroupsIntro) {
                    dispatch(setDeviceSettingsKey('didViewGroupsIntro', true));
                }
                // The app pops the create flow and then opens the group. On web those
                // separate actions get out of step with the browser history and leave
                // the intro open, so the stack is replaced in one action instead.
                navigation.dispatch((state) => {
                    const flowStart = state.routes.findIndex((route) => CREATE_FLOW_ROUTES.includes(route.name));
                    const routes = [
                        ...state.routes.slice(0, flowStart === -1 ? state.routes.length - 1 : flowStart),
                        { name: 'Group', params: { id }, key: `Group-${id}-${Date.now()}` },
                    ];
                    return CommonActions.reset({
                        ...state,
                        routes,
                        index: routes.length - 1,
                    });
                });
                tryAdvertiseExtraMembers?.({
                    potentialExtraMembers: selectedContacts,
                    wasAddingToDisplayName: `${groupName} Group`,
                    suggestGroupId: id,
                });
                return true;
            },
        });
    }, [
        colors.text.textOnBrand,
        didViewGroupsIntro,
        group,
        name,
        selectedContacts,
        tryAdvertiseExtraMembers,
    ]);
    const user = useAppSelector(selectUser);
    const onAdd = useCallback((item) => {
        if (item.handle?.toLowerCase() === user.urlHandle.toLowerCase()) {
            Modal.showError(group ? strings.addError : strings.shareError, () => { }, 'Hold on 💫', 'Okay');
            return;
        }
        onPressInvite(item);
    }, [group, onPressInvite, user.urlHandle]);
    const renderItem = useCallback(({ item, index, section }) => {
        const formattedHandle = item.handle?.toLowerCase();
        const formattedItem = { ...item, handle: formattedHandle };
        const active = formattedHandle && !!selectedContactsObj[formattedHandle];
        let title = strings.buttonInvite;
        const isInGroup = groupAnalytics?.users.find((u) => u.handle?.toLowerCase() === formattedHandle);
        if (item.isEmmaUser)
            title = strings.buttonAdd;
        if (active)
            title = strings.buttonRemove;
        return (<FlatListItemCard index={index} style={{ backgroundColor: colors.cards.onDark }} itemsLength={section.data.length}>
          <ContactItem contact={formattedItem}>
            {isInGroup ? (<Text Text-14 Gray-50 style={styles.added}>
                {strings.buttonAdded}
              </Text>) : (<Button {...(active ? { gray: true } : { brandReversed: true })} hug square title={title} onPress={() => active
                    ? removeFromSelected(formattedItem)
                    : onAdd(formattedItem)} style={styles.button}/>)}
          </ContactItem>
        </FlatListItemCard>);
    }, [
        colors.cards.onDark,
        groupAnalytics?.users,
        onAdd,
        removeFromSelected,
        selectedContactsObj,
    ]);
    const scrollHandler = useAnimatedScrollHandler((handle) => {
        scrollY.value = handle.contentOffset.y;
    }, []);
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    const renderContent = useMemo(() => {
        let ListEmptyComponent = null;
        if (searchFilter && !sections.length && !isLoading) {
            ListEmptyComponent = (<ListEmptyView happy alt style={styles.empty} onCard title="No results" subTitle="Try searching for another @EmmaTag"/>);
        }
        if (!searchFilter) {
            ListEmptyComponent = (<ListEmptyView happy alt style={styles.empty} onCard title={strings.emptyTitle} subTitle={strings.emptySubTitle}/>);
        }
        if (!sections.length && isLoading) {
            ListEmptyComponent = <ListLoader smallMargin/>;
        }
        return (<AnimatedSectionList renderSectionHeader={({ section: { title } }) => (<SectionHeader verticalMargins horizontalMargins onLight>
            {title}
          </SectionHeader>)} sections={sections} onScroll={scrollHandler} stickySectionHeadersEnabled={false} renderItem={renderItem} keyExtractor={keyExtractor} keyboardShouldPersistTaps="handled" keyboardDismissMode="on-drag" ListEmptyComponent={ListEmptyComponent} contentContainerStyle={contentContainerStyle} showsVerticalScrollIndicator scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}/>);
    }, [
        contentContainerStyle,
        isLoading,
        renderItem,
        scrollHandler,
        searchFilter,
        sections,
    ]);
    const searchBarRef = useRef(null);
    const onChangeSearchTerm = useCallback((newValue) => {
        handleSearch(newValue);
    }, [handleSearch]);
    const textInputStyle = useAnimatedStyle(() => ({
        backgroundColor: interpolateColor(scrollY.value, [0, 40], [
            colors.cards.onDark,
            colors.background.dark,
        ]),
    }), [colors.background.dark, colors.cards.onDark, scrollY]);
    return (<AppView withFrame={false}>
      <StatusBar barStyle="dark-content"/>
      <NavigationHeader title="Add friends" scrollY={scrollY}>
        <SelectedContactsList onPress={removeFromSelected} contacts={selectedContacts}/>
        <View style={paddingHorizontalStyle}>
          <SearchBar ref={searchBarRef} placeholder="Search @EmmaTag" textInputBgStyles={textInputStyle} containerStyle={styles.search} onChangeSearchTerm={onChangeSearchTerm} onCancel={handleReset} autoCapitalize="none"/>
        </View>
      </NavigationHeader>

      {renderContent}

      <Button brand floating onPress={handleSave} title={strings.buttonContinue} isFetching={isFetching}/>
    </AppView>);
};
const styles = StyleSheet.create({
    search: {
        marginBottom: rem(4),
    },
    button: {
        marginLeft: rem(12),
        paddingHorizontal: rem(8),
    },
    empty: {
        marginTop: rem(8),
    },
    added: {
        marginRight: rem(15),
    },
});
